import { useCallback, useState } from 'react';

import { downloadTeamsReportRequest } from '../../../api/reports/teamsReports';
import { getReportErrorMessage, ReportErrorMessage } from '../utils';

export interface ReportDownloadError extends ReportErrorMessage {
  requestId: string;
  status?: number;
}

/**
 * The presigned link lives 15 minutes, so it is fetched on every click and opened at once,
 * never kept. The tab is opened synchronously in the click so the browser doesn't block it
 * as a pop-up once the request has resolved.
 *
 * A refusal is returned rather than toasted: a recipient may read a report and still be
 * refused the file (403, a section needs a right they lack) — the page shows why in place.
 */
export const useReportDownload = (companyId?: string, onStale?: (requestId: string) => void) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<ReportDownloadError | null>(null);

  const download = useCallback(
    async (requestId: string) => {
      if (!companyId) return;
      const tab = window.open('', '_blank');
      setDownloadingId(requestId);
      setError(null);
      try {
        const { data } = await downloadTeamsReportRequest(companyId, requestId);
        if (tab) {
          tab.opener = null;
          tab.location.href = data.url;
        } else {
          window.location.assign(data.url);
        }
      } catch (e: any) {
        tab?.close();
        const status: number | undefined = e?.response?.status;
        let message = getReportErrorMessage(e);
        if (status === 409) message = { title: 'This report isn’t ready yet', description: 'Try again shortly.' };
        if (status === 410)
          message = { title: 'This report has expired', description: 'Reports are kept for 30 days.' };
        setError({ ...message, requestId, status });
        // 409 (not ready) and 410 (expired) mean the row on screen is stale — reload it
        if (status === 409 || status === 410) onStale?.(requestId);
      } finally {
        setDownloadingId(null);
      }
    },
    [companyId, onStale]
  );

  return { download, downloadingId, error, clearError: () => setError(null) };
};
